import Loading from 'components/loading'
import { IAgencyGroup } from 'pages/agency-group-management/interface'
import React, { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { headerAgencyActivityTable } from './header'

import { useQuery } from '@apollo/client'

import IconHoverCopy from 'assets/images/icon-hover-copy.svg'

import DropdownBase, { Option } from 'components/partials/dropDown'
import { notify } from 'components/toastify'
import { PAGINATION_DEFAULT, styleJsonPacket } from 'constants/common'
import { MESSAGES, STATUS } from 'constants/messages'
import { useSession } from 'helpers/checkSession'
import { isEqual } from 'lodash'
import {
  GET_AGENCY_ACTIVITY_BY_AGENCY_GROUP,
  IAgencyActivity
} from 'queries/admin/agencyActivity'
// import DetailAgencyActivityLog from '../agency-activity-detail/detailAgencyActivityLogDialog'
import { onHandleCopyItem } from 'helpers/functionUtils'

interface Props {
  currentAgencyGroup: IAgencyGroup
  activityType: 'FORWARD_WEBHOOK' | 'FORWARD_EMAIL'
  isLoading: boolean
  hasMoreData: boolean
  handleRefetch: () => void
  handleFetchMore: () => void
}

const statusDropdownOptions: IFilterDropdown[] = [
  {
    id: 'ALL',
    name: 'ALL',
  },
  {
    id: 'SUCCESS',
    name: 'SUCCESS',
  },
  {
    id: 'REQUEST',
    name: 'REQUEST',
  },
  {
    id: 'FAILED',
    name: 'FAILED',
  },
  {
    id: 'RETRYING',
    name: 'RETRYING',
  },
]

interface IFilterDropdown extends Option {}

const AgencyActivityTable: React.FC<Props> = ({
  activityType,
  currentAgencyGroup,
  isLoading,
  hasMoreData,
  handleFetchMore,
}) => {
  const { handleErrorSession } = useSession()
  const [agencyGroup, setAgencyGroup] = useState(currentAgencyGroup)
  const [agencyActivities, setAgencyActivities] = useState<IAgencyActivity[]>([])
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL')

  const { refetch } = useQuery(GET_AGENCY_ACTIVITY_BY_AGENCY_GROUP, {
    variables: {
      agencyGroupId: agencyGroup.agencyGroupId,
      activityType: 'FORWARD_WEBHOOK',
      skip: PAGINATION_DEFAULT.SKIP,
      take: PAGINATION_DEFAULT.TAKE,
      status: null as null | string,
    },
    onCompleted: (result) => {
      if (!isEqual(agencyActivities, result.getAgencyActivitiesByAgencyGroup)) {
        setAgencyActivities(result.getAgencyActivitiesByAgencyGroup)
      }
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (currentAgencyGroup) {
      setAgencyGroup(currentAgencyGroup)
    }
  }, [currentAgencyGroup])

  const handleChangeOptionStatus = (option: IFilterDropdown) => {
    setSelectedStatusFilter(option.name)
  }

  const onHandleCopyData = (data: string) => {
    onHandleCopyItem(data, MESSAGES.INFO.I_COPIED_CONTENT, MESSAGES.ERROR.E_SOMETHING_WENT_WRONG)
  }

  useEffect(() => {
    if (selectedStatusFilter) {
      refetch({
        agencyGroupId: currentAgencyGroup.agencyGroupId,
        skip: PAGINATION_DEFAULT.SKIP,
        take: PAGINATION_DEFAULT.TAKE,
        activityType,
        status: selectedStatusFilter === 'ALL' ? null : selectedStatusFilter,
      })
    }
  }, [selectedStatusFilter])

  return (
    <div>
      <div className="flex flex-col items-end pb-2">
        <span>Filter by Status</span>
        <DropdownBase
          className="w-[200px]"
          options={statusDropdownOptions}
          handleOptions={handleChangeOptionStatus}
          initialValue={'ALL'}
        />
      </div>
      <table className="w-full text-left border-2 border-neutral-6 rounded-t-lg overflow-hidden">
        <thead className="bg-primary-shade4 text-body1 text-neutral-2 md:pr-4">
          <tr>
            {headerAgencyActivityTable.map((item: any, idx) => (
              <th key={idx} className={item.headerStyles}>
                <div className={`flex `}>
                  <span className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}>{item.name}</span>
                  <div className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}></div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>

      {isLoading && <Loading className="relative py-6" height={30} width={30} />}

      {agencyActivities.length === 0 && !isLoading ? (
        <p className="py-6 text-center text-body1 text-neutral-4">No data available.</p>
      ) : (
        <InfiniteScroll
          dataLength={agencyActivities.length}
          next={handleFetchMore}
          hasMore={hasMoreData}
          height={'max-content'}
          style={{ maxHeight: 300, minHeight: 200 }}
          loader={''}
          endMessage={''}
          className="cus-scrollbar"
        >
          {agencyActivities?.map((data: IAgencyActivity, index: number) => (
            <table key={index} className="cursor-pointer w-full">
              <tbody>
                <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                  <td className={` ${headerAgencyActivityTable[0].rowWid}`}>
                    <div>
                      <span>{data.agencyActivityId}</span>
                    </div>
                  </td>
                  <td className={`${headerAgencyActivityTable[1].rowWid}`}>
                    <div className="relative pr-10">
                      <span style={{ ...styleJsonPacket }}>{JSON.stringify(data.request)}</span>
                      <button
                        title="Copy this Request"
                        onClick={(e) => {
                          e.stopPropagation()
                          onHandleCopyData(JSON.stringify(data.request))
                        }}
                      >
                        <img
                          className="absolute top-[5px] right-0"
                          src={IconHoverCopy}
                          alt="Icon-Hover-Copy"
                        />
                      </button>
                    </div>
                  </td>
                  <td className={`${headerAgencyActivityTable[2].rowWid}`}>
                    <div className="relative pr-10">
                      <span style={{ ...styleJsonPacket }}>{JSON.stringify(data.response)})</span>
                      <button
                        title="Copy this Response"
                        onClick={(e) => {
                          e.stopPropagation()
                          onHandleCopyData(JSON.stringify(data.response))
                        }}
                      >
                        <img
                          className="absolute top-[5px] right-0"
                          src={IconHoverCopy}
                          alt="Icon-Hover-Copy"
                        />
                      </button>
                    </div>
                  </td>
                  <td className={`${headerAgencyActivityTable[3].rowWid}`}>
                    <div>
                      <span>{data.status}</span>
                    </div>
                  </td>
                  <td className={`${headerAgencyActivityTable[4].rowWid}`}>
                    <div>
                      <span>{data.createdAt}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          ))}
        </InfiniteScroll>
      )}
    </div>
  )
}

export default AgencyActivityTable
